// =============================================================================
// MfaNudgeBanner — non-blocking 2FA recommendation for desktop sellers.
//
// On mount, calls listFactors. If the user does NOT have a verified TOTP
// factor, renders an amber-toned "Set up 2FA" recommendation card above the
// dashboard. Dismissible per-session (no persistence — it'll show again on
// the next visit).
//
// Mirrors mobile/src/features/seller/components/MfaNudgeBanner.tsx. Fail-
// closed on the nudge: loading state → null, RPC error → null. We only
// render once we have positive evidence the user has no factor, so we never
// show this card to someone who already enrolled.
// =============================================================================

import { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { C } from "../lib/theme";
import { supabase } from "../lib/supabase";

export function MfaNudgeBanner({ onSetup }) {
  // null  = still loading or fetch failed (fail-closed → don't render)
  // false = user has no verified factor (render the banner)
  // true  = user has a verified factor (don't render)
  const [hasFactor, setHasFactor] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (error || !data) {
        setHasFactor(true); // fail-closed: never nag on a fetch failure
        return;
      }
      const verified = data.totp?.some((t) => t.status === "verified") ?? false;
      setHasFactor(verified);
    })();
    return () => { cancelled = true; };
  }, []);

  if (hasFactor === null || hasFactor === true || dismissed) {
    return null;
  }

  return (
    <div
      style={{
        background: C.amberBg,
        borderRadius: 14,
        padding: 16,
        marginBottom: 16,
        border: `1px solid ${C.amberBorder}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <ShieldCheck size={20} color={C.amber} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: C.amberDk,
            marginBottom: 4,
          }}
        >
          Protect your store
        </div>
        <div
          style={{
            fontSize: 12,
            color: C.amberDk,
            lineHeight: 1.5,
            marginBottom: 10,
            opacity: 0.85,
          }}
        >
          Set up two-factor authentication so you don't lose access to your earnings.
        </div>
        <button
          type="button"
          onClick={onSetup}
          aria-label="Set up two-factor authentication"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: C.amber,
            color: C.white,
            border: "none",
            borderRadius: 10,
            padding: "8px 14px",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Set up 2FA →
        </button>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss 2FA recommendation"
        style={{
          flexShrink: 0,
          background: "none",
          border: "none",
          padding: 2,
          cursor: "pointer",
          color: C.amberDk,
          display: "inline-flex",
        }}
      >
        <X size={16} color={C.amberDk} />
      </button>
    </div>
  );
}
